import React, { useEffect, useState } from 'react';
import { isNil } from 'lodash';
import Checkbox from 'shared/components/andtComponents/Checkbox';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import Tooltip from 'shared/components/andtComponents/Tooltip';
import Button from 'shared/components/andtComponents/Button';
import CustomCSVDownload from 'shared/components/buttons/CustomCSVDownload';
import Pagination from 'shared/components/andtComponents/Pagination';
import { palette } from 'shared/constants/colorsConstants';
import UsersIcon from './UsersIcon';
import AddUsers from './userModals/AddUsersModal/AddUsers';
import { useUsersContext } from './contexts/usersContext';
import { Search } from '../components/Search';
import { DEFAULT_PAGE_SIZE, GET_USERS_QUERY_PARAMS } from '../consts';
import { fetchUsers } from 'users/new-user-management/hooks/reactQuery/apiUsers';
import OrgToCustomersSwitch from 'users/containers/Organization/components/OrgToCustomersSwitch/OrgToCustomersSwitch';
import { useRootStore } from 'app/contexts/RootStoreContext';

import styles from './UsersHeader.module.scss';

const UsersHeader = ({
  currentPage,
  isNextPageLoading,
  pageChanged,
  onCustomerSelectionChanged,
  onOrgCustomersToggle,
}) => {
  const {
    isAllExpanded,
    isCustomerTokensLoading,
    isCustomersView,
    selectedCustomer,
    setIsAllExpanded,
    setShowFilters,
    setTableFilters,
    showFilters,
    tableFilters,
    totalUsers,
  } = useUsersContext();

  const [filterByEnabled, setFilterByEnabled] = useState(tableFilters?.[GET_USERS_QUERY_PARAMS.ENABLED.name] === true);
  const [filterByDisabled, setFilterByDisabled] = useState(
    tableFilters?.[GET_USERS_QUERY_PARAMS.ENABLED.name] === false,
  );

  const fetchExportData = async () => {
    const data = await fetchUsers(tableFilters);
    if (data?.users) {
      const usersWithRoles = data.users.map((user) => {
        const roles = user?.roles?.map((role) => role.displayName).join(', ') || '';
        return { ...user, roles };
      });
      return [{ data: usersWithRoles, filename: 'users.csv' }];
    }
  };

  const totalPages = totalUsers >= 0 ? Math.ceil(totalUsers / DEFAULT_PAGE_SIZE) : 0;

  const userStatusFilterChange = (enabled) => {
    if (
      (isNil(tableFilters?.[GET_USERS_QUERY_PARAMS.ENABLED.name]) && isNil(enabled)) ||
      tableFilters?.[GET_USERS_QUERY_PARAMS.ENABLED.name] === enabled
    ) {
      return;
    }
    if (isNil(enabled)) {
      const { [GET_USERS_QUERY_PARAMS.ENABLED.name]: _, ...rest } = tableFilters; // eslint-disable-line no-unused-vars
      setTableFilters(rest);
    } else {
      setTableFilters({ ...tableFilters, [GET_USERS_QUERY_PARAMS.ENABLED.name]: enabled });
    }
  };

  useEffect(() => {
    if ((!filterByEnabled && !filterByDisabled) || (filterByEnabled && filterByDisabled)) {
      userStatusFilterChange(null);
    } else if (filterByEnabled) {
      userStatusFilterChange(true);
    } else if (filterByDisabled) {
      userStatusFilterChange(false);
    }
  }, [filterByEnabled, filterByDisabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPageChange = (newPage) => {
    pageChanged(newPage - currentPage);
  };

  const searchChanged = (search) => {
    if (search) {
      setTableFilters({
        ...tableFilters,
        [GET_USERS_QUERY_PARAMS.SEARCH.name]: search,
      });
    } else {
      const { [GET_USERS_QUERY_PARAMS.SEARCH.name]: _, ...rest } = tableFilters; // eslint-disable-line no-unused-vars
      setTableFilters(rest);
    }
  };

  const { usersStore } = useRootStore();

  const isRightSideHeaderDisabled = isCustomersView && (!selectedCustomer?.name || isCustomerTokensLoading);

  return (
    <div className={styles.headerContainer}>
      <div className={styles.headerLeft}>
        <div className={styles.expandAndTitle}>
          <Button
            automationid="expand-rows"
            text=""
            isSecondary
            icon={() => <GenerateIcon iconName={isAllExpanded ? ICONS.collapse.name : ICONS.expand.name} />}
            onClick={() => {
              setIsAllExpanded(!isAllExpanded);
            }}
          />
          <div className={styles.header}>
            <UsersIcon />
            <div className={styles.title}>
              Users <span className={styles.count}>({totalUsers})</span>
            </div>
          </div>
        </div>
        <div className={styles.filters}>
          <Checkbox
            isChecked={filterByEnabled}
            onChange={() => {
              setFilterByEnabled(!filterByEnabled);
            }}
            primary
            text="Enabled"
          />
          <Checkbox
            isChecked={filterByDisabled}
            primary
            onChange={() => {
              setFilterByDisabled(!filterByDisabled);
            }}
            text="Disabled"
          />
        </div>
        {usersStore.isCurrentUserReseller ? (
          <OrgToCustomersSwitch
            isCustomersView={isCustomersView}
            onOrgCustomersToggle={onOrgCustomersToggle}
            selectedCustomer={selectedCustomer}
            onCustomerSelectionChanged={onCustomerSelectionChanged}
          />
        ) : null}
      </div>
      {isRightSideHeaderDisabled ? null : (
        <div className={styles.headerRight}>
          <AddUsers disabled={isCustomersView && !selectedCustomer?.name} />
          <Search search={tableFilters?.[GET_USERS_QUERY_PARAMS.SEARCH.name]} setSearch={searchChanged} isDebounce />
          <Tooltip title="Filter columns value" arrow>
            <Button
              text="Filter Results"
              automationid="users-filter-results"
              onClick={() => {
                setShowFilters(!showFilters);
              }}
              overrideStyles={showFilters ? { background: palette.blue[200] } : {}}
              isSecondary
              icon={() => (
                <GenerateIcon
                  iconName={showFilters ? ICONS.xMark.name : ICONS.filterList.name}
                  className={styles.filtersIcon}
                />
              )}
            />
            <span />
          </Tooltip>
          <Tooltip title="Export" arrow>
            <CustomCSVDownload
              fetchData={fetchExportData}
              automationid="export-users"
              showDownloadIcon
              isLoading={false}
              filesNumber={1}
              isSecondary
              isLightButton
              hideText
              style={{ height: 36 }}
            />
            <span />
          </Tooltip>
          <Pagination
            currPageIdx={currentPage}
            isNextPageLoading={isNextPageLoading}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
};


export default UsersHeader;
